import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, Github, Linkedin, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { contact } from "../mock";
import Reveal from "./Reveal";
import SectionHeader from "./SectionHeader";

const initialForm = { name: "", email: "", subject: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const contactInfo = [
    { icon: Mail, label: "Email", value: contact.email, href: `mailto:${contact.email}` },
    { icon: Phone, label: "Phone", value: contact.phone, href: `tel:${contact.phone}` },
    { icon: MapPin, label: "Location", value: contact.location },
  ];

  const socialLinks = [
    { icon: Github, label: "GitHub", url: contact.github },
    { icon: Linkedin, label: "LinkedIn", url: contact.linkedin },
  ];

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) {
      setErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
    }
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name";
    if (!form.email.trim()) next.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Enter a valid email address";
    if (!form.subject.trim()) next.subject = "Please add a subject";
    if (form.message.trim().length < 10) next.message = "Message should be at least 10 characters";
    return next;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSending(true);
    setTimeout(() => {
      const subject = encodeURIComponent(form.subject);
      const body = encodeURIComponent(
        `Hi Adeel,\n\n${form.message}\n\n— ${form.name}\n${form.email}`,
      );
      window.location.href = `mailto:${contact.email}?subject=${subject}&body=${body}`;
      setSending(false);
      toast.success("Email client opened — looking forward to hearing from you!");
      setForm(initialForm);
    }, 700);
  };

  const inputClass = (hasError) =>
    `h-11 rounded-xl border bg-background px-4 text-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 ${
      hasError
        ? "border-destructive/60 focus-visible:ring-destructive"
        : "border-border focus-visible:border-primary/50 focus-visible:ring-primary"
    }`;

  return (
    <section id="contact" className="relative pt-8 md:pt-10 pb-14 md:pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="06."
          eyebrow="Contact"
          title="Let&apos;s work together"
          description="Have a project in mind, a role to fill, or just want to talk AI? My inbox is always open."
        />

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Info column */}
          <div className="space-y-5">
            <Reveal>
              <p className="flex items-center gap-2 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 px-5 py-4 text-emerald-400 text-sm md:text-base">
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                </span>
                {contact.availability}
              </p>
            </Reveal>

            <Reveal delay={80} className="space-y-4">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className={`group flex items-center gap-4 rounded-2xl border border-border bg-card p-4 transition-all duration-300 hover:border-primary/40 ${
                    info.href ? "cursor-target cursor-pointer hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/5" : ""
                  }`}
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-md transition-transform duration-300 group-hover:scale-105">
                    <info.icon size={22} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {info.label}
                    </div>
                    {info.href ? (
                      <a
                        href={info.href}
                        className="cursor-target break-all text-sm md:text-base font-medium text-foreground hover:text-primary transition-colors"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <div className="text-sm md:text-base font-medium text-foreground">{info.value}</div>
                    )}
                  </div>
                  {info.href && (
                    <span className="ml-auto text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
                      <Send size={14} />
                    </span>
                  )}
                </div>
              ))}
            </Reveal>

            <Reveal delay={140}>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Follow me:</span>
                <div className="flex gap-3">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cursor-target border border-border text-muted-foreground hover:text-primary hover:border-primary/50 p-2.5 rounded-xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/10"
                      aria-label={social.label}
                    >
                      <social.icon size={22} />
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div className="rounded-2xl border border-border/70 bg-secondary/40 p-5 flex items-start gap-3">
                <Clock size={18} className="text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <span className="text-foreground font-medium">Quick response.</span> I typically
                  reply within 24 hours. For urgent matters, message me on LinkedIn.
                </p>
              </div>
            </Reveal>
          </div>

          {/* Form column */}
          <Reveal delay={100}>
            <form
              onSubmit={handleSubmit}
              noValidate
              className="glow-border rounded-3xl bg-card/60 backdrop-blur-sm p-6 md:p-8 space-y-5"
            >
              <div>
                <h3 className="font-display text-xl font-semibold">Send me a message</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Fill in a few details and I&apos;ll get back to you.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="mb-2 block text-sm font-medium">
                    Full name <span className="text-destructive">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Jane Doe"
                    className={`cursor-target w-full ${inputClass(!!errors.name)}`}
                  />
                  {errors.name && (
                    <p className="mt-1.5 text-xs text-destructive flex items-center gap-1.5">
                      <CheckCircle2 size={12} /> {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-medium">
                    Email <span className="text-destructive">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="jane@company.com"
                    className={`cursor-target w-full ${inputClass(!!errors.email)}`}
                  />
                  {errors.email && (
                    <p className="mt-1.5 text-xs text-destructive flex items-center gap-1.5">
                      <CheckCircle2 size={12} /> {errors.email}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="mb-2 block text-sm font-medium">
                  Subject <span className="text-destructive">*</span>
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="Project discussion, opportunity, collaboration…"
                  className={`cursor-target w-full ${inputClass(!!errors.subject)}`}
                />
                {errors.subject && (
                  <p className="mt-1.5 text-xs text-destructive flex items-center gap-1.5">
                    <CheckCircle2 size={12} /> {errors.subject}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="mb-2 block text-sm font-medium">
                  Message <span className="text-destructive">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell me about your project, goals, and timeline…"
                  className={`cursor-target w-full min-h-[120px] rounded-xl border bg-background p-4 text-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 resize-y ${
                    errors.message
                      ? "border-destructive/60 focus-visible:ring-destructive"
                      : "border-border focus-visible:border-primary/50 focus-visible:ring-primary"
                  }`}
                />
                {errors.message && (
                  <p className="mt-1.5 text-xs text-destructive flex items-center gap-1.5">
                    <CheckCircle2 size={12} /> {errors.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={sending}
                className="cursor-target group relative w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white font-semibold h-12 disabled:opacity-70 transition-all duration-300 hover:shadow-lg hover:shadow-teal-600/30"
              >
                {sending ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Preparing…
                  </>
                ) : (
                  <>
                    Send Message
                    <Send
                      size={18}
                      className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5"
                    />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;